import Vue from 'vue';

import type {
  FetchOptions,
} from '@/scripts/Common/api/types';

import {
  responseType,
} from "@/scripts/Common/api/enums";

const apiNameMap = {
  deleteCards: '/api/v1/admin/card/delete',
  getCardList: '/api/v1/admin/card/list',
  getUnunsedCardNumbers: '/api/v1/admin/card/unused-cardnumbers',
  getUnassignedCardNumbers: '/api/v1/admin/issuer/card/unassigned',
  updateOrCreateCards: '/api/v1/admin/card',
  getIssuerCardList: '/api/v1/admin/issuer/card/list',
  updateOrCreateIssuerCards: '/api/v1/admin/issuer/card',
  deleteIssuerCards: '/api/v1/admin/issuer/card/delete',
  importCards: '/api/v1/admin/import/cards',
  getConfiguredWebchargesByIssuerId: '/api/v1/admin/webcharges/configured/issuer/list',
  deleteIssuerWebcharges: '/api/v1/admin/webcharges/configured/delete',
  configuredWebchargeResource: '/api/v1/admin/webcharges/configured',
  webchargeConfiguration: '/api/v1/admin/webcharge/configuration/issuer',
  cardLimtieConfiguration: '/api/v1/admin/card/limite',
  gratificationConfiguration: '/api/v1/admin/webcharge/configuration/gratification',
  getProfipayCardStates: '/api/v1/admin/card/states',
  getWebchargeStates: '/api/v1/admin/webcharge/protocol/state/list',
  repeatLastWebcharge: '/api/v1/admin/webcharge/protocol/{protocolId}/repeat',
  sepaTerminalSettingsResource: '/api/v1/admin/sepa/configuration/terminal',
  sepaOtherSettingsResource: '/api/v1/admin/sepa/configuration/other',
  sepaIssuerSettingsResource: '/api/v1/admin/sepa/configuration/issuer',
  sepa: '/api/v1/admin/sepa',
  sepaDownload: '/api/v1/admin/sepa/{sepaExportId}/download',
  sepaSaleReset: '/api/v1/admin/sepa/{sepaExportId}/reset',
  sepaWebchargeReset: '/api/v1/admin/sepa/webcharges/{sepaExportId}/reset',
  cardUser: '/api/v1/admin/card-users',
  customers: '/api/v1/admin/customers',
  customerResource: '/api/v1/admin/customers/{customerId}',
  customerStateChange: '/api/v1/admin/customers/{action}',
  defaultProvision: '/api/v1/admin/customers/default-provision',
  customerAppSettings: '/api/v1/admin/customers/initialShopAppSettings/{accountingPartnerId}',
  accountingPartners: '/api/v1/admin/customers/accountingPartners',
  importCustomers: '/api/v1/admin/import/customers',
  service: '/api/v1/admin/service',
  issuers: '/api/v1/admin/issuers',
  issuersWithParams: '/api/v1/admin/issuers/{param}',
  passwordReset: '/api/v1/admin/account/{group}/{id}/password/reset',
  operator: '/api/v1/admin/operator',
  creditorIdValidation: '/api/v1/admin/bank-account/validate/creditor-id',
  cardBalanceReport: '/api/v1/admin/report/card-balance-report/{deadline}',
  chargeLimitReport: '/api/v1/admin/report/issuer/chargelimit-report',
  chargeReport: '/api/v1/admin/report/issuer/webcharge-report',
  trxErrorReport: '/api/v1/admin/report/error/trx-count',
  salesReport: '/api/v1/admin/report/sales',
  vatDetails: '/api/v1/admin/vat-details',
  issuerIdentityUsers: '/api/v1/admin/issuers/{issuerId}/identity/users',
  identityUsers: '/api/v1/admin/identity/users/{userId}',
  changeIdentityUsersState: '/api/v1/admin/identity/users/{userId}/{state}',
  identityUserCreatePasswordChangeTicket: '/api/v1/admin/identity/users/{userId}/password/change/ticket',
  identityUserResetPasswordViaEmail: '/api/v1/admin/identity/users/{userId}/password/change/email',
};

Object.freeze(apiNameMap);

const replacePlaceholders = (input: string, replacements: Record<string, string>) => {
  return input.replace(/{(\w+)}/g, (text, key) => replacements[key] || text);
}

const createFetchDefaults = ({apiName, options = {}}: { apiName: keyof typeof apiNameMap, options: FetchOptions }) => {
  const gatewayUrl = Vue.prototype.$config.GatewayUrl;

  let route = apiNameMap[apiName];
  if (options.routeParams) {
    route = replacePlaceholders(route, options.routeParams);
  }

  const url = new URL(`${gatewayUrl}${route}`);

  const {
    method = 'GET',
    query = undefined,
  } = options;

  if (
    query !== null
    && typeof query === 'object'
    && !Array.isArray(query)
  ) {

    for (const [key, value] of Object.entries(query)) {
      //.net apis require query arrays in the following format: value=element1&value=element2&value=element3
      if (Array.isArray(value)) {
        value.forEach(element => url.searchParams.append(key, element))
        continue;
      }

      if ((value !== undefined && value !== null)) {
        // @ts-ignore
        url.searchParams.set(key, value)
      }
    }
  }

  return {
    method,
    url,
  }
};

const defaultFetch = async ({
                              apiName,
                              bearer,
                              body = undefined,
                              options = {},
                              resultType = responseType.json
                            }: {
  apiName: keyof typeof apiNameMap,
  bearer: string,
  body?: any,
  options?: FetchOptions,
  resultType?: responseType
}) => {
  let error = undefined;
  let response = undefined;

  const {
    method,
    url
  } = createFetchDefaults({apiName, options});

  const {
    headers
  } = options

  if (headers) {
    headers['Authorization'] = `Bearer ${bearer}`;
  }

  const defaultHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${bearer}`
  };

  try {
    response = await fetch(url.href,
      {
        method,
        headers: headers ?? defaultHeaders,
        body: body,
        signal: options.abortSignal,
      });

  } catch (error_) {
    return {
      error: error_
    }
  }

  if (!response.ok) {

    let errorJson = {message: 'server_error'};

    try {
      errorJson = await response.json();
    } catch {
    }

    return {
      error: errorJson
    }
  }

  const paginationLimit = Number.parseInt(response.headers.get('Pagination-Limit') ?? '10', 10) ?? 0;
  const paginationCount = Number.parseInt(response.headers.get('Pagination-Count') ?? '10', 10) ?? 0;
  const paginationPage = Number.parseInt(response.headers.get('Pagination-Page') ?? '1', 10) ?? 0;
  const totalItemCount = Number.parseInt(response.headers.get('Total-Item-Count') ?? '0', 10) ?? 0;


  let result: any;
  try {
    switch (resultType) {
      case responseType.json: {
        result = await response.json();
        break;
      }
      case responseType.text: {
        result = await response.text();
        break;
      }
      default: {
        result = await response.json();
      }
    }
  } catch (e) { /* Ignored */
  }

  return {
    error,
    paginationCount,
    paginationLimit,
    paginationPage,
    totalItemCount,
    response: result,
  }
};

export {
  defaultFetch,
}
